import React, { Fragment, useEffect, useState } from 'react';
import { Button, Col, Container, Input, Row, Table } from 'reactstrap';
import { toast } from "react-toastify";
import _ from 'lodash';
import { FilterSelect, ServiceThreadSpinner } from '.';
import { api, filterHelpers } from '../utils';
import { confirmAlert } from "react-confirm-alert";

export default function ManageCARProblems() {
   const ALL_OPTS = { value: 0, label: "-- Select --", active: false };
   const CAR_MODEL = { value: 0, label: "", active: true };
   const [carList, setCarList] = useState([]);
   const [carPblm, setCarPblm] = useState(CAR_MODEL);
   const [editPblm, setEditPblm] = useState("");
   const [newPblm, setNewPblm] = useState("");
   const [origPblm, setOrigPblm] = useState("");
   const [spinCarPblm, setSpinCarPblm] = useState(true);
   const [filters, setFilters] = useState([
      { filterName: "problem", value: ALL_OPTS }
   ]);

   useEffect(() => {
      runQuery();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [])

   useEffect(() => {
      if (!carPblm || carPblm.value === 0)
         return; // problem was reset to initial state

      if (!editPblm || editPblm.length < 1)
         return; // Not editing       

      if (carPblm.label === origPblm)
         return; // No change made to problem being edited

      if (carPblm.label.length < 3) { // Do we need this?
         toast.error("Problem description must be at least 3 characters");
         return;
      }
      updateCarPblm();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [carPblm])

   function activateCarProblem() {
      if (!carPblm || carPblm.value === 0) {
         toast.error("Please select a problem type to activate");
         return;
      }

      setSpinCarPblm(true);
      api.post(`Quality/ActivateCarProblem/${carPblm.value}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("Problem type activated!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinCarPblm(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinCarPblm(false);
         });
   }

   function deleteCarProblem() {
      setSpinCarPblm(true);
      api.post(`Quality/DeleteCarProblem/${carPblm.value}`)
         .then((response) => {
            if (response.data.success) {
               toast.success("Problem type was successfully deleted or made inactive!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinCarPblm(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinCarPblm(false);
         });
   }

   function onCarProblemChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      setNewPblm(""); // Clear any lingering values
      if (newFilterValue.value === 0) {
         setCarPblm(CAR_MODEL);
         setOrigPblm("");
         setEditPblm("");
      }
      else { // Get the problem to be edited
         const d = _.find(carList, (x) => x.value === newFilterValue.value);
         let changes = Object.assign({}, carPblm);
         changes.label = newFilterValue.label;
         changes.active = d.active;
         changes.value = newFilterValue.value;
         setCarPblm(changes);
         setOrigPblm(newFilterValue.label);
         setEditPblm(newFilterValue.label);
      }
   }

   function onCarPblmUpdate() {
      let changes = Object.assign({}, carPblm);
      changes.label = editPblm
      setCarPblm(changes);
   }

   function runQuery() {
      setSpinCarPblm(true);
      api
         .fetch(`Quality/LoadCorrectiveActionProblems`)
         .then((response) => {
            if (response.data.success) {
               const dfList = _.concat([ALL_OPTS],
                  _.map(response.data.list, (d) => ({
                     value: d.value,
                     label: d.label,
                     active: d.active
                  })));
               return { dfList: dfList };
            } else {
               toast.error(response.data.message);
            }
         })
         .then(({ dfList }) => {
            setCarList(dfList);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setEditPblm("");
            setOrigPblm("");
            setNewPblm("")
            setCarPblm(CAR_MODEL);
            setSpinCarPblm(false);
         });
   }

   function saveNewCarPblm() {
      if (!newPblm || newPblm.length < 1) {
         toast.error("Please provide a problem description");
         return;
      }
      if (newPblm.length < 3) {
         toast.error("Problem description must be at least 3 characters");
         return;
      }
      let payload = Object.assign({}, CAR_MODEL);
      payload.label = newPblm;

      setSpinCarPblm(true);
      api.post('Quality/SaveCarProblem', payload)
         .then((response) => {
            if (response.data.success) {
               toast.success("Problem type created!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinCarPblm(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinCarPblm(false);
         });
   }

   function updateCarPblm() {
      setSpinCarPblm(true);
      api.post('Quality/SaveCarProblem', carPblm)
         .then((response) => {
            if (response.data.success) {
               toast.success("Problem type updated!")
               runQuery();
            } else {
               toast.error(response.data.message);
               setSpinCarPblm(false);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
            setSpinCarPblm(false);
         });
   }

   const showDeleteModal = () => {
      if (!carPblm || carPblm.value === 0) {
         toast.error("Please select a problem");
         return;
      }
      confirmAlert({
         title: "Confirm Deletion",
         message: `Are you sure you want to delete the ${editPblm} problem type?`,
         buttons: [
            { label: "Yes", onClick: () => { deleteCarProblem() } },
            { label: "No" }
         ]
      });
   }

   return (
      <Container fluid>
         <Row>
            <Col>
               <Fragment>
                  <ServiceThreadSpinner on={spinCarPblm}>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           {carList && carList.length ? (
                              <Table className="transparent-background my-0">
                                 <tbody>
                                    <tr>
                                       <td colSpan={3} className="transparent-background data-cell noWrap h6">
                                          These problem types are used in Corrective, Preventative, and Supplier Actions
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell noWrap h6" width="80px">
                                          Problem:
                                       </td>
                                       <td className="transparent-background" width="500px">
                                          <FilterSelect
                                             filterName="problem"
                                             options={carList}
                                             value={filterHelpers.getValue(filters, "problem")}
                                             onChangeCallback={e => onCarProblemChange(e)}
                                          />
                                       </td>
                                       <td className="transparent-background"></td>
                                    </tr>
                                 </tbody>
                              </Table>
                           ) : null}
                        </Col>
                     </Row>

                     {carPblm && carPblm.value > 0 ? (
                        <div>
                           <Row className="px-0 py-0">
                              <Table className="transparent-background data-table">
                                 <tbody>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td colSpan={5} className="transparent-background data-cell noWrap h6">
                                          Deleting a problem type that has corrective actions attached will set it to inactive.
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td colSpan={5} className="transparent-background data-cell noWrap h6">
                                          Inactive problem types will show on action edit pages, but will be unable to be selected. They show only for the sake of history records that might need it.
                                       </td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td className="transparent-background data-cell width95px noWrap h6">
                                          Id: {carPblm.value}
                                       </td>
                                       <td className="transparent-background data-cell noWrap h6 width115px">
                                          Active: {carPblm.active ? "Yes" : "No"}
                                       </td>
                                       <td colSpan={3} className="transparent-background data-cell"></td>
                                    </tr>
                                    <tr>
                                       <td className="transparent-background data-cell width40px"></td>
                                       <td className="transparent-background data-cell noWrap h6 width115px">
                                          Problem:
                                       </td>
                                       <td className="transparent-background data-cell width500px">
                                          <Input
                                             type="text"
                                             value={editPblm || ""}
                                             onChange={(e) => setEditPblm(e.target.value)}
                                             maxLength="100"
                                          />
                                       </td>
                                       <td className="transparent-background data-cell width115px">
                                          <Button
                                             title="Update"
                                             className="shredButton"
                                             onClick={() => onCarPblmUpdate()}
                                          >
                                             Update
                                          </Button>
                                       </td>
                                       <td className="transparent-background data-cell width115px">
                                          <Button
                                             title="Delete"
                                             className="shredButton"
                                             onClick={() => showDeleteModal()}
                                          >
                                             Delete
                                          </Button>
                                       </td>
                                       {!carPblm.active && carPblm.value !== 0 ? (
                                          <td className="transparent-background data-cell">
                                             <Button
                                                title="Reactivate"
                                                className="shredButton"
                                                onClick={() => activateCarProblem()}
                                             >
                                                Reactivate
                                             </Button>
                                          </td>
                                       ) : <td className="transparent-background data-cell"></td>}
                                    </tr>
                                 </tbody>
                              </Table>
                           </Row>
                        </div>
                     ) : null}

                     <br />
                     <br />
                     <hr />
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           Add New Problem Type
                        </Col>
                     </Row>
                     <Row className="px-2 py-2">
                        <Col xxl={{ offset: 0, size: 12 }}>
                           <Table className="transparent-background my-0">
                              <tbody>
                                 <tr>
                                    <td className="transparent-background data-cell noWrap h6" width="80px">
                                       Problem:
                                    </td>
                                    <td className="transparent-background" width="500px">
                                       <Input
                                          type="text"
                                          value={newPblm || ""}
                                          onChange={(e) => setNewPblm(e.target.value)}
                                          maxLength="100"
                                       />
                                    </td>
                                    <td className="transparent-background">
                                       <Button
                                          title="New Problem"
                                          className="shredButtonExcel ms-2"
                                          onClick={() => saveNewCarPblm()}
                                       >
                                          Add Problem
                                       </Button>
                                    </td>
                                 </tr>
                              </tbody>
                           </Table>
                        </Col>
                     </Row>
                  </ServiceThreadSpinner>
               </Fragment>
            </Col>
         </Row>
      </Container>
   );
}
